/**
 * @file categories.ts
 * @description Centralized category taxonomy catalog and helpers.
 * Defines single source of truth for timer classification, discovery categories,
 * and platform seed taxonomies.
 * @author [F. Andy Seidl](https://www.linkedin.com/in/faseidl/)
 * @license UNLICENSED
 */

import type { CategoryDefinition } from './index.js';

/**
 * Canonical 12 Category catalog defining initial platform taxonomy.
 */
export const CANONICAL_CATEGORIES: readonly CategoryDefinition[] = Object.freeze([
  {
    id: 'holidays',
    label: 'Holidays & Seasons',
    description: 'Countdowns to seasonal celebrations, national holidays, and festivals',
    icon: '🎁',
    sortOrder: 1,
    isActive: true,
  },
  {
    id: 'milestones',
    label: 'Life Milestones',
    description: 'Personal milestones, anniversaries, weddings, retirement, and life achievements',
    icon: '💖',
    sortOrder: 2,
    isActive: true,
  },
  {
    id: 'tech',
    label: 'Tech & Products',
    description: 'Hardware releases, software launches, developer keynotes, and product drops',
    icon: '💻',
    sortOrder: 3,
    isActive: true,
  },
  {
    id: 'gaming',
    label: 'Gaming & Esports',
    description: 'Game releases, major tournaments, esports championships, and season launches',
    icon: '🎮',
    sortOrder: 4,
    isActive: true,
  },
  {
    id: 'creators',
    label: 'Creators & Streams',
    description: 'Livestreams, creator premieres, podcasts, and digital content broadcasts',
    icon: '📹',
    sortOrder: 5,
    isActive: true,
  },
  {
    id: 'sports',
    label: 'Sports & Events',
    description: 'Matches, games, playoffs, derbies, and athletic showdowns',
    icon: '🏆',
    sortOrder: 6,
    isActive: true,
  },
  {
    id: 'fitness',
    label: 'Fitness & Streaks',
    description: 'Workout streaks, fasting windows, race days, and personal training goals',
    icon: '🔥',
    sortOrder: 7,
    isActive: true,
  },
  {
    id: 'community',
    label: 'Community & Culture',
    description: 'Local meetups, religious observances, community events, and civic dates',
    icon: '🤝',
    sortOrder: 8,
    isActive: true,
  },
  {
    id: 'entertainment',
    label: 'Entertainment',
    description: 'Movie releases, concert tours, album debuts, and comic conventions',
    icon: '🎬',
    sortOrder: 9,
    isActive: true,
  },
  {
    id: 'education',
    label: 'Education',
    description: 'Exams, graduation ceremonies, semester deadlines, and academic calendars',
    icon: '📖',
    sortOrder: 10,
    isActive: true,
  },
  {
    id: 'science-space',
    label: 'Science & Space',
    description: 'Rocket launches, astronomical events, eclipse viewings, and space missions',
    icon: '🚀',
    sortOrder: 11,
    isActive: true,
  },
  {
    id: 'general',
    label: 'General',
    description: 'Custom events, personal countdowns, and miscellaneous occasions',
    icon: '⏱️',
    sortOrder: 12,
    isActive: true,
  },
]);

/**
 * Historical category identifier aliases mapped to active canonical IDs.
 */
export const CATEGORY_ALIASES: Readonly<Record<string, string>> = Object.freeze({
  'weddings-milestones': 'milestones',
  'community-faith': 'community',
  streamers: 'creators',
});

/**
 * Known legacy keyword icon mappings to Unicode emoji representations.
 */
export const LEGACY_CATEGORY_ICON_MAP: Readonly<Record<string, string>> = Object.freeze({
  gift: '🎁',
  heart: '💖',
  laptop: '💻',
  gamepad: '🎮',
  video: '📹',
  trophy: '🏆',
  flame: '🔥',
  users: '🤝',
  handshake: '🤝',
  film: '🎬',
  'book-open': '📖',
  rocket: '🚀',
  clock: '⏱️',
});

/**
 * Resolves a category icon identifier or symbol to a displayable icon (Unicode emoji or inline SVG).
 * If the input is already an emoji or SVG, it is returned directly.
 * If the input is a known legacy identifier (e.g. 'gift', 'laptop'), it maps to the canonical emoji.
 * Defaults to '⏱️' when undefined or empty.
 *
 * @param icon - Raw icon string from category catalog or database table.
 * @returns Formatted emoji or SVG icon string.
 */
export function resolveCategoryIcon(icon?: string | null): string {
  if (!icon || !icon.trim()) {
    return '⏱️';
  }
  const trimmed = icon.trim();
  const mapped = LEGACY_CATEGORY_ICON_MAP[trimmed.toLowerCase()];
  if (mapped) {
    return mapped;
  }
  return trimmed;
}

/**
 * Fast lookup map for canonical categories by ID.
 */
const CATEGORY_MAP = new Map<string, CategoryDefinition>(
  CANONICAL_CATEGORIES.map((category) => [category.id, category]),
);

/**
 * Retrieves a canonical category by its unique ID or known historical alias.
 *
 * @param id - Category slug identifier.
 * @returns Matched CategoryDefinition or undefined.
 */
export function getCategoryById(id: string): CategoryDefinition | undefined {
  const canonicalId = CATEGORY_ALIASES[id] ?? id;
  return CATEGORY_MAP.get(canonicalId);
}

/**
 * Asserts that a category identifier belongs to the canonical platform taxonomy.
 *
 * @param id - Category slug identifier.
 * @returns Validated canonical category ID.
 * @throws {Error} If the category identifier is not in the canonical taxonomy.
 */
export function validateCanonicalCategory(id: string): string {
  const matched = getCategoryById(id);
  if (!matched) {
    throw new Error(
      `Unrecognized category: "${id}". Must be one of the canonical categories: ${CANONICAL_CATEGORIES.map((c) => c.id).join(', ')}.`,
    );
  }
  return matched.id;
}
